import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Paper, Stack } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../../../components/common/PageHeader";
import ActionIconButton from "../../../../components/common/ActionIconButton";
import PageLoader from "../../../../components/common/PageLoader";
import ChipList from "../../../../components/common/ChipList";
import TruncatedText from "../../../../components/common/TruncatedText";
import EmptyState from "../../../../components/common/EmptyState";
import SearchFilterBar from "../../../../components/common/SearchFilterBar";
import DataTable from "../../../../components/DataTable";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import { ROLE_SCOPES } from "../../constants";
import { systemRoleService, tenantRoleService } from "../../services";
import useAuth from "../../../../hooks/useAuth";
import {
  collectAllowSuggestions,
  filterAllowByCatalog,
  matchesRoleSearch,
  normalizeRoleList,
  pickRoleId,
} from "../../utils/normalizeRole";
import RoleFormDialog from "../RoleFormDialog";
import { tenantPermissionService } from "../../../permission-management/services";
import { toPermissionCatalogNames } from "../../../permission-management/utils/normalizePermission";

const RoleManagement = ({
  scope = ROLE_SCOPES.SYSTEM,
  roleService: roleApi,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { activeTenantId, tenantId } = useAuth();
  const isSystem = scope === ROLE_SCOPES.SYSTEM;
  const requestTenantId = activeTenantId || tenantId;
  const listBase = isSystem ? "/roles" : "/tenant-roles";
  const api = roleApi || (isSystem ? systemRoleService : tenantRoleService);

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [appliedName, setAppliedName] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  // Tenant: use live permission catalog only (do not merge role.allow — deleted
  // permissions would keep showing). System: no catalog API, suggest from roles.
  const allowSuggestions = useMemo(() => {
    if (!isSystem) {
      const seen = new Set();
      return (permissionOptions || []).filter((name) => {
        const value = String(name || "").trim();
        if (!value || seen.has(value)) return false;
        seen.add(value);
        return true;
      });
    }
    return collectAllowSuggestions(roles);
  }, [isSystem, permissionOptions, roles]);

  const loadPermissionOptions = useCallback(async () => {
    if (isSystem) {
      setPermissionOptions([]);
      return;
    }
    setPermissionsLoading(true);
    try {
      const payload = await tenantPermissionService.getAllTenantPermissions(
        requestTenantId
      );
      setPermissionOptions(toPermissionCatalogNames(payload));
    } catch {
      setPermissionOptions([]);
    } finally {
      setPermissionsLoading(false);
    }
  }, [isSystem, requestTenantId]);

  const loadRoles = useCallback(
    async (name = appliedName) => {
      setLoading(true);
      setError("");
      try {
        const payload = isSystem
          ? await (api?.getAllSystemRoles || systemRoleService.getAllSystemRoles)()
          : await tenantRoleService.getAllTenantRoles(requestTenantId);
        const normalized = normalizeRoleList(payload).filter((role) =>
          matchesRoleSearch(role, { name })
        );
        setRoles(normalized);
      } catch (err) {
        const message = getApiErrorMessage(err);
        setRoles([]);
        setError(message);
        dispatch(appActions.apiErrorMessage(message));
      } finally {
        setLoading(false);
      }
    },
    [api, appliedName, dispatch, isSystem, requestTenantId]
  );

  useEffect(() => {
    loadRoles(appliedName);
  }, [appliedName, loadRoles]);

  useEffect(() => {
    if (!isSystem) {
      loadPermissionOptions();
    }
  }, [isSystem, loadPermissionOptions]);

  const openDetails = useCallback(
    (row) => {
      // Prefer Role Id from Get All for update/delete routes.
      const id = pickRoleId(row) || row?.name;
      if (!id) return;
      navigate(`${listBase}/view/${encodeURIComponent(id)}`, {
        state: { role: row },
      });
    },
    [listBase, navigate]
  );

  const openCreate = async () => {
    setFormOpen(true);
    setPermissionOptions([]);
    await loadPermissionOptions();
  };

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      if (isSystem) {
        await (api?.createSystemRole || systemRoleService.createSystemRole)(
          payload
        );
      } else {
        const name = String(payload.name || "").trim();
        const permissions = Array.isArray(payload.permissions)
          ? payload.permissions.filter(Boolean).map(String)
          : Array.isArray(payload.allow)
            ? payload.allow.filter(Boolean).map(String)
            : [];

        // API create is name-only; assign permissions via update using roleId.
        const created = await tenantRoleService.createTenantRole(
          { name },
          requestTenantId
        );
        let roleId = pickRoleId(created, created?.data, created?.result);

        if (!roleId && name) {
          try {
            const byName = await tenantRoleService.getTenantRoleByName(
              name,
              requestTenantId
            );
            roleId = pickRoleId(byName);
          } catch {
            roleId = "";
          }
        }

        if (!roleId && name) {
          const list = normalizeRoleList(
            await tenantRoleService.getAllTenantRoles(requestTenantId)
          );
          const match = list.find(
            (item) =>
              String(item.name || "").toLowerCase() === name.toLowerCase()
          );
          roleId = pickRoleId(match);
        }

        if (permissions.length) {
          if (!roleId) {
            throw new Error(
              "Role created, but Role Id was not returned. Open the role and add permissions from Edit."
            );
          }
          try {
            await tenantRoleService.updateTenantRole(
              roleId,
              { permissions },
              requestTenantId
            );
          } catch (permErr) {
            // Name-only create already succeeded; roll back so we don't leave an empty role.
            try {
              await tenantRoleService.deleteTenantRole(roleId, requestTenantId);
            } catch {
              // Ignore delete failure — still report permission error.
            }
            throw permErr;
          }
        }
      }
      dispatch(appActions.apiSuccessMessage("Role created successfully."));
      setFormOpen(false);
      await loadRoles(appliedName);
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Role Name",
        flex: 1,
        minWidth: 160,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "allow",
        headerName: "Permissions",
        flex: 1.6,
        minWidth: 220,
        sortable: false,
        valueGetter: (value, row) => {
          const items = isSystem
            ? row?.allow || []
            : filterAllowByCatalog(row?.allow, permissionOptions);
          return Array.isArray(items) ? items.join(", ") : "";
        },
        renderCell: (params) => {
          const items = isSystem
            ? params.row.allow
            : filterAllowByCatalog(params.row.allow, permissionOptions);
          return <ChipList items={items} maxVisible={2} />;
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <ActionIconButton
            title="View Details"
            icon={<VisibilityOutlinedIcon fontSize="small" />}
            onClick={() => openDetails(params.row)}
          />
        ),
      },
    ],
    [isSystem, openDetails, permissionOptions]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ mb: 1 }}
      >
        <PageHeader
          title="Roles"
          subtitle={
            isSystem
              ? "Manage system roles and their permissions."
              : "Manage tenant roles and their permissions."
          }
          sx={{ mb: 0 }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={openCreate}
          >
            Add Role
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => loadRoles(appliedName)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <SearchFilterBar
        fields={[
          {
            key: "name",
            label: "Role Name",
            value: nameFilter,
            onChange: (e) => setNameFilter(e.target.value),
            width: 280,
          },
        ]}
        onSearch={() => setAppliedName(nameFilter.trim())}
        onClear={() => {
          setNameFilter("");
          setAppliedName("");
        }}
      />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          minHeight: 360,
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading && !roles.length ? (
          <PageLoader text="Loading roles..." />
        ) : !loading && !roles.length ? (
          <EmptyState
            title="No roles found"
            description="No roles found for the current filters."
          />
        ) : (
          <DataTable
            rows={roles}
            columns={columns}
            pageSize={20}
            showPagination
            loading={loading}
            autoHeight
            getRowId={(row) => pickRoleId(row) || row.roleId || row.name}
            paginationMode="client"
          />
        )}
      </Paper>

      <RoleFormDialog
        open={formOpen}
        mode="create"
        role={null}
        allowSuggestions={allowSuggestions}
        permissionsLoading={permissionsLoading}
        loading={saving}
        tenantMode={!isSystem}
        onClose={() => setFormOpen(false)}
        onSubmit={handleCreate}
      />
    </Box>
  );
};

export default RoleManagement;
